import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Beds, Cancellation, Delay, DropDown, GreenTick, Guest, HotelSample, Icici, Location, Lost, Sqaure } from '../../../assests/ExportImageIcon'
import { setBookingGuests, setBookingResult } from '../../../redux/hotelSearch/hotelSearchSlice'
import DatePicker from '../../../componants/datePicker/DatePicker'
import SearchableSelect from '../../../componants/searchableSelect/SearchableSelect'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../componants/toast/toastService'
import { guestPlanFromOccupancy, resolveOccupancy } from '../../../utils/hotelOccupancy'
import './hotelBookingDetail.css'

const ADDONS = [
    { key: 'breakfast', label: 'Add Breakfast for ₹289 for all guests' },
    { key: 'breakfastLunch', label: 'Add Breakfast + Lunch for ₹2252 for all guests' },
]

const IMPORTANT_INFO = [
    'Primary guest must present a valid government ID at check-in',
    'Early check-in and late check-out are subject to availability',
    'Extra guest charges may apply as per hotel policy',
    'Cancellation timelines depend on the selected rate plan',
]

const COUPONS = [
    { code: 'GIRUSH', off: '₹ 250 off', desc: 'Use this coupon and get instant discount on your hotel booking.', applied: true },
    { code: 'GIRUSH', off: '₹ 312 off', desc: 'Use this coupon and get instant discount on your hotel booking.' },
    { code: 'GIRUSH', off: '₹ 312 off', desc: 'Use this coupon and get instant discount on your hotel booking.' },
    { code: 'GIRUSH', off: '₹ 312 off', desc: 'Use this coupon and get instant discount on your hotel booking.' },
]

const TRIP_SECURE_BENEFITS = [
    { icon: Lost, title: 'Lost Baggage Assistance', subtext: '24x7 Support' },
    { icon: Cancellation, title: 'Common Carrier Cancellation', subtext: '₹999 upto discount' },
    { icon: Delay, title: 'Common Carrier Delay', subtext: '₹499 Flat Off' },
]

const PHONE_CODES = ['+91', '+1', '+44', '+971', '+61', '+65']

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** DOB cannot be today or a future date — max selectable is yesterday */
const maxDobIso = () => {
    const date = new Date()
    date.setDate(date.getDate() - 1)
    return date.toISOString().split('T')[0]
}

const isValidEmail = (email) => EMAIL_REGEX.test(String(email || '').trim())

const isValidPhone = (phone) => /^\d{10}$/.test(String(phone || '').replace(/\D/g, ''))

const isValidDob = (dob) => {
    if (!dob) return false
    const max = maxDobIso()
    return String(dob) <= max
}

const emptyAdult = () => ({
    title: 'Mr',
    first_name: '',
    last_name: '',
    email: '',
    phone_code: '+91',
    phone: '',
    gender: '',
    nationality: '',
    dob: '',
})

const emptyChild = () => ({
    title: 'Master',
    first_name: '',
    last_name: '',
    email: '',
    phone_code: '+91',
    phone: '',
    gender: '',
    nationality: '',
    dob: '',
})

/** Guest forms follow the per-room occupancy chosen on hotel search. */
const buildRoomGuestPlan = (searchParams) => guestPlanFromOccupancy(resolveOccupancy(searchParams))

const createInitialGuests = (plan) => {
    const guests = {}
    plan.forEach((room, index) => {
        guests[String(index + 1)] = {
            adult: Array.from({ length: room.adultCount }, emptyAdult),
            child: Array.from({ length: room.childCount }, emptyChild),
        }
    })
    return guests
}

const isAdultValid = (guest) => (
    guest.title
    && guest.first_name?.trim()
    && guest.last_name?.trim()
    && isValidEmail(guest.email)
    && guest.phone_code
    && isValidPhone(guest.phone)
    && guest.gender
    && guest.nationality
    && isValidDob(guest.dob)
)

const isChildValid = (guest) => (
    guest.title
    && guest.first_name?.trim()
    && guest.last_name?.trim()
    && guest.gender
    && guest.nationality
    && isValidDob(guest.dob)
)

const areAllGuestsValid = (guestsByRoom) => {
    const rooms = Object.values(guestsByRoom || {})
    if (!rooms.length) return false
    return rooms.every((room) => {
        const adultsOk = (room.adult || []).every(isAdultValid)
        const childrenOk = (room.child || []).every(isChildValid)
        return adultsOk && childrenOk && (room.adult || []).length > 0
    })
}

const FieldError = ({ children }) => (
    children ? <p className='hotelGuestFieldError'>{children}</p> : null
)

const GoldStar = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M7 10.65 2.89 12.95 3.8 8.33.34 5.13l4.68-.55L7 .29l1.98 4.29 4.68.55-3.46 3.2.92 4.62Z" fill="#F5A623" />
    </svg>
)

const formatBookingDate = (value) => {
    if (!value) return '—'
    const date = new Date(`${value}T00:00:00`)
    if (Number.isNaN(date.getTime())) return value
    const day = date.toLocaleDateString('en-GB', { day: '2-digit' })
    const month = date.toLocaleDateString('en-GB', { month: 'short' })
    const year = date.getFullYear()
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' })
    return `${day} ${month} ${year}, ${weekday}`
}

const getNightCount = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 1
    const start = new Date(`${checkIn}T00:00:00`)
    const end = new Date(`${checkOut}T00:00:00`)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1
    const diff = Math.round((end - start) / (1000 * 60 * 60 * 24))
    return Math.max(1, diff)
}

const formatPrice = (price, currency = 'INR') => {
    const amount = Number(price)
    if (Number.isNaN(amount)) return price || '—'
    const symbol = currency === 'INR' ? '₹' : `${currency} `
    return `${symbol}${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

const HotelBookingDetail = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const searchParams = useSelector((state) => state.hotelSearch.searchParams)
    const results = useSelector((state) => state.hotelSearch.results)
    const selectedHotel = useSelector((state) => state.hotelSearch.selectedHotel)
    const hotelDetail = useSelector((state) => state.hotelSearch.hotelDetail)
    const selectedRoom = useSelector((state) => state.hotelSearch.selectedRoom)

    const [travellerOpen, setTravellerOpen] = useState(true)
    const [selectedAddon, setSelectedAddon] = useState(null)
    const [tripSecureChoice, setTripSecureChoice] = useState('yes')
    const [guestsByRoom, setGuestsByRoom] = useState({})
    const [paying, setPaying] = useState(false)
    const [countries, setCountries] = useState([])

    useEffect(() => {
        service.get(endpoints.users.countries, {}, { silent: true }).then(({ data }) => {
            const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
            setCountries(list)
        }).catch(() => {
            setCountries([])
        })
    }, [])

    const phoneCodeOptions = useMemo(() => {
        const fromApi = countries
            .map((c) => c.dial_code || c.phone_code || c.phoneCode)
            .filter(Boolean)
            .map((code) => (String(code).startsWith('+') ? String(code) : `+${code}`))
        const merged = [...new Set([...fromApi, ...PHONE_CODES])]
        return merged.length ? merged : PHONE_CODES
    }, [countries])

    const nationalityOptions = useMemo(() => (
        countries.map((c) => ({
            id: c.id,
            value: c.code || c.iso2 || c.iso_code || c.name || String(c.id),
            label: c.name || c.country_name || c.code || 'Country',
        })).filter((c) => c.value)
    ), [countries])

    const dobMax = maxDobIso()

    const hotelInfo = hotelDetail?.hotelInfo || null
    const room = selectedRoom?.room || null
    const option = selectedRoom?.option || null

    const hotelName = hotelInfo?.name || selectedHotel?.name || selectedRoom?.hotelName || 'Hotel'
    const hotelRating = Number(hotelInfo?.rating ?? selectedHotel?.rating ?? 0)
    const starCount = Number.isNaN(hotelRating) ? 0 : Math.max(0, Math.min(5, Math.round(hotelRating)))
    const address = hotelInfo?.address || selectedHotel?.address || {}
    const locationLabel = [address.city, address.country || address.address]
        .filter(Boolean)
        .join(', ')
        || selectedHotel?.location
        || address.address
        || '—'

    const nights = getNightCount(searchParams.checkIn, searchParams.checkOut)
    const adults = Number(searchParams.adults) || 1
    const roomsCount = Math.max(1, Number(searchParams.rooms) || 1)
    const children = Number(searchParams.children) || 0
    const guestsLabel = [
        `${adults} Adult${adults > 1 ? 's' : ''}`,
        children > 0 ? `${children} Child${children > 1 ? 'ren' : ''}` : null,
        `${roomsCount} Room${roomsCount > 1 ? 's' : ''}`,
    ].filter(Boolean).join(' • ')

    const roomPlan = useMemo(
        () => buildRoomGuestPlan(searchParams),
        [searchParams]
    )

    useEffect(() => {
        setGuestsByRoom(createInitialGuests(roomPlan))
    }, [roomPlan])

    const guestsValid = useMemo(() => areAllGuestsValid(guestsByRoom), [guestsByRoom])

    const roomTitle = room?.title || 'Room'
    const roomImage = room?.images?.[0] || selectedHotel?.thumbnail || HotelSample
    const roomSpecs = useMemo(() => {
        if (Array.isArray(room?.specs) && room.specs.length) {
            return room.specs.map((spec) => ({
                icon: spec.icon || Sqaure,
                text: String(spec.text || '').replace(/^sleeps\b/i, 'Sleeps'),
            }))
        }
        const specs = []
        const raw = room?.raw || {}
        if (raw.room_size) specs.push({ icon: Sqaure, text: String(raw.room_size) })
        if (raw.max_occupancy) {
            const occ = String(raw.max_occupancy).split('|t|')[0]
            if (occ) specs.push({ icon: Guest, text: `${occ} Guests` })
        }
        if (raw.bed_type) {
            const bed = String(raw.bed_type).split('|t|')[0]
            if (bed) specs.push({ icon: Beds, text: bed })
        }
        if (raw.description) specs.push({ icon: Beds, text: raw.description })
        return specs.length ? specs : [
            { icon: Guest, text: `${adults} Guests` },
            { icon: Beds, text: 'Standard bedding' },
        ]
    }, [room, adults])

    const amenities = (room?.facilities || []).filter(Boolean).slice(0, 6)
    const planTitle = option?.name || 'Room Only'
    const perks = Array.isArray(option?.perks) && option.perks.length
        ? option.perks
        : [option?.name, room?.raw?.description].filter(Boolean)
    const refundStatus = option?.refundStatus || 'Non-refundable'
    const refundNote = option?.isRefundable
        ? 'Cancellation charges may apply as per hotel policy'
        : 'Refund is not applicable for this booking'
    const cancellationRuleLabels = Array.isArray(option?.cancellationRules)
        ? option.cancellationRules.map((rule) => {
            if (!rule) return ''
            if (rule.refund_percentage != null) return `${rule.title} (${rule.refund_percentage}% refund)`
            return rule.title || ''
        }).filter(Boolean)
        : []

    // Room rate from hotel room API is already priced for the searched occupancy
    // (all rooms in searchParams). payment_summary.room_price uses the same total —
    // do NOT multiply by roomsCount again (that double-charged multi-room stays).
    const packageRoomPrice = Number(option?.rawPrice)
    const fallbackPrice = Number(selectedHotel?.price)
    const totalRoomPrice = !Number.isNaN(packageRoomPrice)
        ? packageRoomPrice
        : (!Number.isNaN(fallbackPrice) ? fallbackPrice : null)
    const unitRoomPrice = totalRoomPrice != null && roomsCount > 0
        ? totalRoomPrice / roomsCount
        : totalRoomPrice
    const currency = option?.currency || selectedHotel?.currency || 'INR'
    const displayUnit = unitRoomPrice != null && !Number.isNaN(unitRoomPrice)
        ? formatPrice(unitRoomPrice, currency)
        : (option?.price || '—')
    const displayTotal = totalRoomPrice != null && !Number.isNaN(totalRoomPrice)
        ? formatPrice(totalRoomPrice, currency)
        : displayUnit

    const updateGuestField = (roomKey, type, guestIndex, field, value) => {
        let nextValue = value
        if (field === 'phone') {
            nextValue = String(value || '').replace(/\D/g, '').slice(0, 10)
        }
        if (field === 'email') {
            nextValue = String(value || '').trimStart()
        }
        if (field === 'dob') {
            // Reject today and future dates
            if (nextValue && nextValue > dobMax) {
                showToast('Date of birth cannot be today or a future date', 'error')
                nextValue = ''
            }
        }
        setGuestsByRoom((prev) => {
            const roomData = prev[roomKey] || { adult: [], child: [] }
            const list = [...(roomData[type] || [])]
            list[guestIndex] = {
                ...list[guestIndex],
                [field]: nextValue,
            }
            return {
                ...prev,
                [roomKey]: {
                    ...roomData,
                    [type]: list,
                },
            }
        })
    }

    const buildGuestPayload = () => {
        const rooms = {}
        Object.keys(guestsByRoom).forEach((roomKey) => {
            const roomData = guestsByRoom[roomKey]
            rooms[roomKey] = {
                adult: (roomData.adult || []).map((guest) => ({
                    title: guest.title,
                    first_name: guest.first_name.trim(),
                    last_name: guest.last_name.trim(),
                    email: guest.email.trim(),
                    phone_code: guest.phone_code,
                    phone: guest.phone.trim(),
                    gender: String(guest.gender || '').toLowerCase(),
                    nationality: guest.nationality,
                    dob: guest.dob,
                })),
                child: (roomData.child || []).map((guest) => ({
                    title: guest.title,
                    first_name: guest.first_name.trim(),
                    last_name: guest.last_name.trim(),
                    email: (guest.email || '').trim(),
                    phone_code: guest.phone_code || '',
                    phone: (guest.phone || '').trim(),
                    gender: String(guest.gender || '').toLowerCase(),
                    nationality: guest.nationality,
                    dob: guest.dob,
                })),
            }
        })
        return { rooms }
    }

    const buildOccupancyPayload = () => (
        roomPlan.map((room, index) => ({
            room_no: index + 1,
            adult: room.adultCount,
            child: room.childCount,
            child_age: room.childAges || [],
        }))
    )
    const handlePay = async () => {
        if (!guestsValid || paying) return

        const accessKeys = hotelDetail?.AccessKeys || {}
        const rateKey = option?.rateKey
            || option?.raw?.rate_key
            || option?.raw?.rateKey
            || room?.raw?.rate_key
            || ''

        if (!rateKey) {
            showToast('Room rate key is missing. Please reselect a room.', 'error')
            return
        }

        const guestPayload = buildGuestPayload()
        const payload = {
            city: searchParams.city || address.city || '',
            country: searchParams.country || address.country || '',
            checkin: searchParams.checkIn || '',
            checkout: searchParams.checkOut || '',
            occupancy: buildOccupancyPayload(),
            requiredCurrency: searchParams.requiredCurrency || currency || 'INR',
            source: selectedHotel?.source || hotelInfo?.source || 'third_party',
            rate_key: rateKey,
            hotel_id: String(
                accessKeys.hotel_id
                || selectedHotel?.id
                || selectedRoom?.hotelId
                || hotelInfo?.id
                || ''
            ),
            session_id: String(
                accessKeys.session_id
                || results?.session_id
                || ''
            ),
            product_id: String(
                accessKeys.product_id
                || selectedHotel?.productId
                || selectedHotel?.product_id
                || ''
            ),
            token_id: String(
                accessKeys.token_id
                || selectedHotel?.tokenId
                || selectedHotel?.token_id
                || ''
            ),
            guests: guestPayload,
        }
        // DB-sourced hotels aren't tied to a third-party search session, so they
        // don't carry a session_id — only require it for third-party bookings.
        if (!payload.hotel_id || (payload.source !== 'db' && !payload.session_id)) {
            showToast('Hotel session expired. Please search again.', 'error')
            return
        }

        setPaying(true)
        try {
            const { data } = await service.post(endpoints.hotel.booking, payload, {
                timeout: 60000,
                silent: true,
            })
            const bookingData = data?.data ?? data
            dispatch(setBookingGuests(guestPayload))
            dispatch(setBookingResult({
                ...bookingData,
                requestPayload: payload,
                bookedAt: new Date().toISOString(),
            }))
            navigate('/hotel-payment')
        } catch {
            // toast handled by service interceptor
        } finally {
            setPaying(false)
        }
    }

    const globalGuestIndex = (() => {
        let adultNo = 0
        let childNo = 0
        return {
            nextAdult: () => {
                adultNo += 1
                return adultNo
            },
            nextChild: () => {
                childNo += 1
                return childNo
            },
        }
    })()

    return (
        <>
            <section className='flightBookingDetails commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='allBookingDetail'>
                                <div className='allBokkingInfo'>
                                    <div className='resultCard'>
                                        <div className='hotelCard__topRow'>
                                            <div>
                                                <div className='hotelCard__name'>{hotelName}</div>
                                                <div className='hotelCard__location mt-2'>
                                                    <img src={Location} alt="" />
                                                    <div className='hotelCard__locationName'>{locationLabel}</div>
                                                </div>
                                            </div>
                                            <div className='hotelCard__stars'>
                                                {Array.from({ length: starCount }).map((_, index) => <GoldStar key={index} />)}
                                            </div>
                                        </div>

                                        <div className='bookingSegmentBox bookingInfoRow'>
                                            <div>
                                                <div className='bookingInfoLabel'>CHECK-IN</div>
                                                <div className='bookingInfoDate'>{formatBookingDate(searchParams.checkIn)}</div>
                                                {/* <div className='bookingInfoTime'>12 PM</div> */}
                                            </div>
                                            <div className='bookingInfoNights'>— {nights} Night{nights > 1 ? 's' : ''} —</div>
                                            <div className='text-end'>
                                                <div className='bookingInfoLabel'>CHECK-OUT</div>
                                                <div className='bookingInfoDate'>{formatBookingDate(searchParams.checkOut)}</div>
                                                {/* <div className='bookingInfoTime'>11 AM</div> */}
                                            </div>
                                        </div>

                                        <div className='bsFareRow mt-2'>
                                            <span>GUEST &amp; ROOMS</span>
                                            <span>{guestsLabel}</span>
                                        </div>
                                    </div>

                                    <div className='fairDetails'>
                                        <div className='roomBookingTop'>
                                            <span className='roomBookingImage'>
                                                <img src={roomImage} alt={roomTitle} />
                                            </span>
                                            {/* <div>
                                                <div className='hotelCard__name'>{roomTitle}</div>
                                                <div className='roomBookingSpecs'>
                                                    {console.log(roomSpecs,">>>> roomSpecs")}
                                                    {roomSpecs.map((spec, index) => (
                                                        <span key={index}><img src={spec.icon} alt="" /> {spec.text}</span>
                                                    ))}
                                                </div>
                                            </div> */}
                                            <div>
    <div className="hotelCard__name">{roomTitle}</div>

    <div className="roomBookingSpecs">
        {roomSpecs.map((spec, index) => {
            let text = spec.text;

            if (text.startsWith("Sleeps")) {
                const guests = text.replace("Sleeps", "").trim();
                text = `${guests} Guests`;
            }

            return (
                <span key={index}>
                    <img src={spec.icon} alt="" /> {text}
                </span>
            );
        })}
    </div>
</div>
                                        </div>

                                        {amenities.length ? (
                                            <div className='roomBookingAmenities'>
                                                {amenities.map((amenity) => (
                                                    <span className='hotelCard__amenityPill' key={amenity}>{amenity}</span>
                                                ))}
                                            </div>
                                        ) : null}

                                        <div className='roomBookingPlanTitle'>{planTitle}</div>
                                        <div className='roomBookingPerks'>
                                            {perks.map((perk, index) => (
                                                <div className='bookingBaggageItem' key={index}>
                                                    <img src={GreenTick} alt="" />
                                                    <span>{perk}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <div className='bsFareDivider' />

                                        <div className='resultCard__airlineName'>{refundStatus}</div>
                                        <div className='resultCard__flightNo'>{refundNote}</div>
                                        {cancellationRuleLabels.length ? (
                                            <ul className='bsInfoList mt-2'>
                                                {cancellationRuleLabels.map((label, index) => (
                                                    <li key={index}>{label}</li>
                                                ))}
                                            </ul>
                                        ) : null}
                                        <button type='button' className='bsCouponApply mt-2'>Inclusions &amp; Cancellation Policy</button>
                                    </div>

                                    {/* <div className='fairDetails'>
                                        <div className='fareDetailsHead tripSecureHead mb-3'>
                                            <div className='bsSectionTitle'>Addons</div>
                                            <span className='addonRecommendedBadge'>RECOMMENDED</span>
                                        </div>
                                        <div className='travellerFieldRow'>
                                            {ADDONS.map((addon) => (
                                                <div
                                                    key={addon.key}
                                                    className={`fareTypeCard ${selectedAddon === addon.key ? 'active' : ''}`}
                                                    onClick={() => setSelectedAddon(addon.key)}
                                                >
                                                    <div className='bsFareRow gap-3'>
                                                        <span>{addon.label}</span>
                                                        <span className={`fareTypeRadio ${selectedAddon === addon.key ? 'checked' : ''}`} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <p className='addonStatNote'>
                                            <span className='addonStatHighlight'>86%</span> travellers enjoyed property's food
                                        </p>
                                    </div> */}

                                    <div className='travellerDetail'>
                                        <div className='fareDetailsHead' onClick={() => setTravellerOpen(!travellerOpen)}>
                                            <div className='bsSectionTitle'>Guest Details</div>
                                            <span className={`fareCollapseIcon ${travellerOpen ? 'open' : ''}`}>&#8963;</span>
                                        </div>

                                        {travellerOpen && (
                                            <div className='fareDetailsBody'>
                                                {Object.keys(guestsByRoom).sort((a, b) => Number(a) - Number(b)).map((roomKey) => {
                                                    const roomGuests = guestsByRoom[roomKey] || { adult: [], child: [] }
                                                    return (
                                                        <div className='hotelGuestRoomBlock' key={roomKey}>
                                                            <div className='hotelGuestRoomTitle'>Room {roomKey}</div>

                                                            {(roomGuests.adult || []).map((guest, guestIndex) => {
                                                                const labelNo = globalGuestIndex.nextAdult()
                                                                return (
                                                                    <div className='bsBox hotelGuestCard' key={`adult-${roomKey}-${guestIndex}`}>
                                                                        <div className='travellerPassengerHead'>
                                                                            <span>Guest {labelNo}</span>
                                                                            <span className='travellerPassengerType'>Adult</span>
                                                                        </div>
                                                                        <div className='travellerFieldRow'>
                                                                            <div className='travellerField'>
                                                                                <label>Title</label>
                                                                                <SearchableSelect
                                                                                    options={[{ value: 'Mr', label: 'Mr' }, { value: 'Mrs', label: 'Mrs' }, { value: 'Ms', label: 'Ms' }, { value: 'Miss', label: 'Miss' }]}
                                                                                    value={guest.title}
                                                                                    onChange={(title) => updateGuestField(roomKey, 'adult', guestIndex, 'title', title)}
                                                                                />
                                                                            </div>
                                                                            <div className='travellerField'>
                                                                                <label>First/Middle Name</label>
                                                                                <input
                                                                                    type='text'
                                                                                    value={guest.first_name}
                                                                                    onChange={(e) => updateGuestField(roomKey, 'adult', guestIndex, 'first_name', e.target.value)}
                                                                                />
                                                                            </div>
                                                                            <div className='travellerField'>
                                                                                <label>Last Name</label>
                                                                                <input
                                                                                    type='text'
                                                                                    value={guest.last_name}
                                                                                    onChange={(e) => updateGuestField(roomKey, 'adult', guestIndex, 'last_name', e.target.value)}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className='travellerFieldRow'>
                                                                            <div className='travellerField'>
                                                                                <label>Gender</label>
                                                                                <SearchableSelect
                                                                                    options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }]}
                                                                                    value={guest.gender}
                                                                                    onChange={(gender) => updateGuestField(roomKey, 'adult', guestIndex, 'gender', gender)}
                                                                                    placeholder='Select'
                                                                                />
                                                                            </div>
                                                                            <div className='travellerField'>
                                                                                <label>Email ID</label>
                                                                                <input
                                                                                    type='email'
                                                                                    inputMode='email'
                                                                                    autoComplete='email'
                                                                                    placeholder='name@example.com'
                                                                                    value={guest.email}
                                                                                    onChange={(e) => updateGuestField(roomKey, 'adult', guestIndex, 'email', e.target.value)}
                                                                                />
                                                                                {guest.email?.trim() && !isValidEmail(guest.email) ? (
                                                                                    <FieldError>Enter a valid email address</FieldError>
                                                                                ) : null}
                                                                            </div>
                                                                            <div className='travellerField'>
                                                                                <label>Phone Number</label>
                                                                                <div className='guestPhoneInput'>
                                                                                    <select
                                                                                        className='guestPhoneCodeSelect'
                                                                                        value={guest.phone_code}
                                                                                        onChange={(e) => updateGuestField(roomKey, 'adult', guestIndex, 'phone_code', e.target.value)}
                                                                                    >
                                                                                        {phoneCodeOptions.map((code) => (
                                                                                            <option key={code} value={code}>{code}</option>
                                                                                        ))}
                                                                                    </select>
                                                                                    <span className='guestPhoneDivider' />
                                                                                    <input
                                                                                        type='tel'
                                                                                        inputMode='numeric'
                                                                                        maxLength={10}
                                                                                        placeholder='10-digit number'
                                                                                        value={guest.phone}
                                                                                        onChange={(e) => updateGuestField(
                                                                                            roomKey,
                                                                                            'adult',
                                                                                            guestIndex,
                                                                                            'phone',
                                                                                            e.target.value
                                                                                        )}
                                                                                    />
                                                                                </div>
                                                                                {guest.phone && !isValidPhone(guest.phone) ? (
                                                                                    <FieldError>Phone number must be 10 digits</FieldError>
                                                                                ) : null}
                                                                            </div>
                                                                        </div>
                                                                        <div className='travellerFieldRow'>
                                                                            <div className='travellerField'>
                                                                                <label>Date Of Birth (DOB)</label>
                                                                                <DatePicker
                                                                                    className='travellerDateInput'
                                                                                    max={dobMax}
                                                                                    value={guest.dob}
                                                                                    onChange={(dob) => updateGuestField(roomKey, 'adult', guestIndex, 'dob', dob)}
                                                                                />
                                                                                {guest.dob && !isValidDob(guest.dob) ? (
                                                                                    <FieldError>DOB cannot be today or a future date</FieldError>
                                                                                ) : null}
                                                                            </div>
                                                                            <div className='travellerField'>
                                                                                <label>Nationality</label>
                                                                                <SearchableSelect
                                                                                    options={nationalityOptions}
                                                                                    value={guest.nationality}
                                                                                    onChange={(nationality) => updateGuestField(roomKey, 'adult', guestIndex, 'nationality', nationality)}
                                                                                    placeholder={nationalityOptions.length ? 'Select nationality' : 'Loading countries…'}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )
                                                            })}

                                                            {(roomGuests.child || []).map((guest, guestIndex) => {
                                                                const labelNo = globalGuestIndex.nextChild()
                                                                return (
                                                                    <div className='bsBox hotelGuestCard' key={`child-${roomKey}-${guestIndex}`}>
                                                                        <div className='travellerPassengerHead'>
                                                                            <span>Guest {labelNo}</span>
                                                                            <span className='travellerPassengerType'>Child</span>
                                                                        </div>
                                                                        <div className='travellerFieldRow'>
                                                                            <div className='travellerField'>
                                                                                <label>Title</label>
                                                                                <SearchableSelect
                                                                                    options={[{ value: 'Master', label: 'Master' }, { value: 'Miss', label: 'Miss' }]}
                                                                                    value={guest.title}
                                                                                    onChange={(title) => updateGuestField(roomKey, 'child', guestIndex, 'title', title)}
                                                                                />
                                                                            </div>
                                                                            <div className='travellerField'>
                                                                                <label>First/Middle Name</label>
                                                                                <input
                                                                                    type='text'
                                                                                    value={guest.first_name}
                                                                                    onChange={(e) => updateGuestField(roomKey, 'child', guestIndex, 'first_name', e.target.value)}
                                                                                />
                                                                            </div>
                                                                            <div className='travellerField'>
                                                                                <label>Last Name</label>
                                                                                <input
                                                                                    type='text'
                                                                                    value={guest.last_name}
                                                                                    onChange={(e) => updateGuestField(roomKey, 'child', guestIndex, 'last_name', e.target.value)}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className='travellerFieldRow'>
                                                                            <div className='travellerField'>
                                                                                <label>Gender</label>
                                                                                <SearchableSelect
                                                                                    options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }]}
                                                                                    value={guest.gender}
                                                                                    onChange={(gender) => updateGuestField(roomKey, 'child', guestIndex, 'gender', gender)}
                                                                                    placeholder='Select'
                                                                                />
                                                                            </div>
                                                                            <div className='travellerField'>
                                                                                <label>Date Of Birth (DOB)</label>
                                                                                <DatePicker
                                                                                    className='travellerDateInput'
                                                                                    max={dobMax}
                                                                                    value={guest.dob}
                                                                                    onChange={(dob) => updateGuestField(roomKey, 'child', guestIndex, 'dob', dob)}
                                                                                />
                                                                                {guest.dob && !isValidDob(guest.dob) ? (
                                                                                    <FieldError>DOB cannot be today or a future date</FieldError>
                                                                                ) : null}
                                                                            </div>
                                                                            <div className='travellerField'>
                                                                                <label>Nationality</label>
                                                                                <SearchableSelect
                                                                                    options={nationalityOptions}
                                                                                    value={guest.nationality}
                                                                                    onChange={(nationality) => updateGuestField(roomKey, 'child', guestIndex, 'nationality', nationality)}
                                                                                    placeholder={nationalityOptions.length ? 'Select nationality' : 'Loading countries…'}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>
                                                    )
                                                })}

                                                {!guestsValid ? (
                                                    <p className='hotelGuestHint'>
                                                        Please complete all guest details. Email must be valid and phone number exactly 10 digits. DOB cannot be today or a future date.
                                                    </p>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>

                                    {/* <div className='travellerDetail'>
                                        <div className='fareDetailsHead tripSecureHead mb-3'>
                                            <div className='bsSectionTitle'>Trip Secure</div>
                                            <img src={Icici} alt='ICICI Lombard' className='tripSecureLogo' />
                                        </div>
                                        <div className='bsBox'>
                                            <div className='tripSecureBenefitsHead'>
                                                <span className='tripSecurePlanBenefits'>PLAN BENEFITS </span>
                                                <span className='tripSecureViewAll'>View All Benefits</span>
                                            </div>
                                            <div className='tripSecureBenefitsRow'>
                                                {TRIP_SECURE_BENEFITS.map((benefit, index) => (
                                                    <div className='tripSecureBenefit' key={index}>
                                                        <img src={benefit.icon} alt="" />
                                                        <div>
                                                            <div className='tripSecureBenefitTitle'>{benefit.title}</div>
                                                            <div className='tripSecureBenefitSub'>{benefit.subtext}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <p className='tripSecureDisclaimer'>
                                                Trip Secure is non-refundable. By selecting it, I confirm all travelers are Indian nationals, aged 6 months to 90 years, and accept the <button type='button' className='tripSecureLink'>T&amp;Cs</button>.
                                            </p>
                                            <div className='tripSecurePrice'>₹ 499 <span>Per Person</span></div>
                                            <label className='tripSecureRadioRow' onClick={() => setTripSecureChoice('yes')}>
                                                <span className={`fareTypeRadio ${tripSecureChoice === 'yes' ? 'checked' : ''}`} />
                                                Yes, Secure my trip.
                                            </label>
                                            <label className='tripSecureRadioRow' onClick={() => setTripSecureChoice('no')}>
                                                <span className={`fareTypeRadio ${tripSecureChoice === 'no' ? 'checked' : ''}`} />
                                                No, I will book without trip secure.
                                            </label>
                                        </div>
                                    </div> */}

                                    <div className='totalPay bgWhite paddingCommon20 borderRadius20 commonBorder'>
                                        <button
                                            type='button'
                                            className='commonBtn w-100'
                                            disabled={!guestsValid || paying}
                                            onClick={handlePay}
                                        >
                                            {paying ? (
                                                <>
                                                    <span className='btnSpinner' aria-hidden="true" />
                                                    Booking…
                                                </>
                                            ) : (
                                                'Countinue to Pay '
                                            )}
                                        </button>
                                        <p className='bsFareNote totalPayNote'>
                                            {guestsValid
                                                ? 'By proceeding, you agree to our Terms & Conditions'
                                                : 'Complete all guest details to continue payment'}
                                        </p>
                                    </div>
                                </div>
                                <div className='bookingSidebar'>
                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>Important Information</div>
                                        <div className='bsBox'>
                                            <ul className='bsInfoList'>
                                                {IMPORTANT_INFO.map((info, index) => (
                                                    <li key={index}>{info}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>
                                            Fare Breakup ({roomsCount} Room{roomsCount > 1 ? 's' : ''})
                                        </div>
                                        <div className='bsBox'>
                                            <div className='bsFareRow'>
                                                <span>Room Fare{roomsCount > 1 ? ` × ${roomsCount}` : ''}</span>
                                                <span>
                                                    {roomsCount > 1
                                                        ? `${displayUnit} × ${roomsCount}`
                                                        : displayUnit}
                                                </span>
                                            </div>
                                            <div className='bsFareRow'>
                                                <span>Taxes &amp; Fees</span>
                                                <span>{option?.taxesNote || '0'}</span>
                                            </div>
                                            <div className='bsFareDivider' />
                                            <div className='bsFareRow bsFareRow--total'>
                                                <span>TOTAL AMOUNT</span>
                                                <span>{displayTotal}</span>
                                            </div>
                                            <p className='bsFareNote'>
                                                {roomsCount > 1
                                                    ? `${displayUnit} per room × ${roomsCount} rooms (rate is for full occupancy).`
                                                    : `${displayUnit} for 1 room.`}
                                                {nights > 1 ? ` · ${nights} nights` : ''}
                                                {' '}Final taxes &amp; fees are confirmed on payment.
                                            </p>
                                        </div>
                                    </div>

                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>Offers &amp; Discounts</div>
                                        <div className='bsBox'>
                                            <input type='text' className='bsCouponInput' placeholder='Enter coupon code' />
                                            {COUPONS.map((coupon, index) => (
                                                <div className='bsCouponCard' key={index}>
                                                    <div className='bsCouponHead'>
                                                        <span className='bsCouponCode'>{coupon.code}</span>
                                                        <span className='bsCouponOff'>{coupon.off}</span>
                                                    </div>
                                                    <p className='bsCouponDesc'>{coupon.desc}</p>
                                                    {coupon.applied
                                                        ? <span className='bsCouponApply'>APPLIED</span>
                                                        : <button type='button' className='bsCouponApply'>APPLY COUPON</button>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}

export default HotelBookingDetail
